import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/SimpleAuthContext';
import { AnnouncementsProvider } from './contexts/AnnouncementsContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/layout/NavbarNew';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Profile from './pages/Profile';
import CareerGuidance from './pages/CareerGuidance';
import CareerAssessmentPage from './pages/CareerAssessmentPage';
import CourseRecommendationsPage from './pages/CourseRecommendationsPage';
import CourseComparisonPage from './pages/CourseComparisonPage';
import Opportunities from './pages/Opportunities';
import OpportunityListPage from './pages/OpportunityListPage';
import CreateOpportunity from './pages/CreateOpportunity';
import EditOpportunity from './pages/EditOpportunity';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import ApplicationManagement from './components/opportunities/ApplicationManagement';
import OpportunityApplication from './components/opportunities/OpportunityApplication';
import ForgotPassword from './pages/auth/ForgotPassword';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Dashboard from './pages/Dashboard';
import CourseApplication from './pages/CourseApplication';
import LmsEnrollmentPending from './pages/LmsEnrollmentPending';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import UserDebug from './pages/UserDebug';
import ConnectivityTest from './pages/ConnectivityTest';

import SimpleDashboard from './pages/SimpleDashboard';
import AuthenticationMessage from './pages/AuthenticationMessage';
import ClearCache from './pages/ClearCache';
import RecruitmentDashboard from './pages/admin/RecruitmentDashboard';

// Test and Debug Components are now in tests/ directory
// These routes are commented out for production - uncomment for development testing
// import IconTest from '../../tests/frontend/react/pages/IconTest';
// import TestErrorHandling from '../../tests/frontend/react/pages/TestErrorHandling';
// import TestLogin from '../../tests/frontend/react/pages/TestLogin';
// import TestSuite from '../../tests/frontend/react/pages/TestSuite';
// import AuthTests from '../../tests/frontend/react/pages/api-tests/AuthTests';
// import APITests from '../../tests/frontend/react/pages/api-tests/APITests';
// import MobileNavTest from '../../tests/frontend/react/pages/MobileNavTest';
// import MobileMenuTest from '../../tests/frontend/react/pages/MobileMenuTest';
// import DebugLogin from '../../tests/debug/DebugLogin';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Help System
import HelpIndex from './pages/help/HelpIndex';
import GettingStarted from './pages/help/user/GettingStarted';
import CoursesHelp from './pages/help/user/CoursesHelp';
import ApplicationsHelp from './pages/help/user/ApplicationsHelp';
import OpportunitiesHelp from './pages/help/user/OpportunitiesHelp';
import CareerGuidanceHelp from './pages/help/user/CareerGuidanceHelp';
import ProfileHelp from './pages/help/user/ProfileHelp';
import DashboardHelp from './pages/help/user/DashboardHelp';
import FAQ from './pages/help/user/FAQ';

// Admin Help (Protected)
import AdminDashboardHelp from './pages/help/admin/AdminDashboardHelp';
import CourseManagementHelp from './pages/help/admin/CourseManagementHelp';
import OpportunityManagementHelp from './pages/help/admin/OpportunityManagementHelp';
import UserAdministrationHelp from './pages/help/admin/UserAdministrationHelp';
import StudentLifecycleHelp from './pages/help/admin/StudentLifecycleHelp';
import AnnouncementsHelp from './pages/help/admin/AnnouncementsHelp';
import DjangoAdminHelp from './pages/help/admin/DjangoAdminHelp';
import SecurityHelp from './pages/help/admin/SecurityHelp';

import './index.css';
import './theme.css';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AnnouncementsProvider>
          <ToastProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <div className="app-shell">
            <Navbar />
            <main className="app-main">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                {/* Test routes commented out for production - uncomment for development */}
                {/* <Route path="/test" element={<IconTest />} /> */}
                {/* <Route path="/test-error" element={<TestErrorHandling />} /> */}
                {/* <Route path="/test-login" element={<TestLogin />} /> */}
                {/* <Route path="/test-suite" element={<TestSuite />} /> */}
                {/* <Route path="/test-auth" element={<AuthTests />} /> */}
                {/* <Route path="/test-api" element={<APITests />} /> */}
                {/* <Route path="/debug-login" element={<DebugLogin />} /> */}
                {/* <Route path="/mobile-nav-test" element={<MobileNavTest />} /> */}
                {/* <Route path="/mobile-menu-test" element={<MobileMenuTest />} /> */}
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/apply/:courseId" element={<CourseApplication />} />
                <Route path="/learning/:publicId" element={
                  <ProtectedRoute>
                    <LmsEnrollmentPending />
                  </ProtectedRoute>
                } />

                {/* Opportunity Routes */}
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/opportunities/browse" element={<OpportunityListPage />} />
                <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
                <Route path="/opportunities/:id/apply" element={
                  <ProtectedRoute>
                    <OpportunityApplication />
                  </ProtectedRoute>
                } />
                <Route path="/opportunities/create" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <CreateOpportunity />
                  </ProtectedRoute>
                } />
                <Route path="/opportunities/:id/edit" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <EditOpportunity />
                  </ProtectedRoute>
                } />
                <Route path="/opportunities/applications" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <ApplicationManagement />
                  </ProtectedRoute>
                } />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* Help System Routes */}
                <Route path="/help" element={<HelpIndex />} />
                <Route path="/help/getting-started" element={<GettingStarted />} />
                <Route path="/help/courses" element={<CoursesHelp />} />
                <Route path="/help/applications" element={<ApplicationsHelp />} />
                <Route path="/help/opportunities" element={<OpportunitiesHelp />} />
                <Route path="/help/career-guidance" element={<CareerGuidanceHelp />} />
                <Route path="/help/profile" element={<ProfileHelp />} />
                <Route path="/help/dashboard" element={<DashboardHelp />} />
                <Route path="/help/faq" element={<FAQ />} />

                {/* Protected Routes */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* Career Guidance Routes */}
                <Route path="/career" element={
                  <ProtectedRoute>
                    <CareerGuidance />
                  </ProtectedRoute>
                } />
                <Route path="/career/assessment" element={
                  <ProtectedRoute>
                    <CareerAssessmentPage />
                  </ProtectedRoute>
                } />
                <Route path="/career/recommendations" element={
                  <ProtectedRoute>
                    <CourseRecommendationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/career/compare" element={
                  <ProtectedRoute>
                    <CourseComparisonPage />
                  </ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <AnalyticsDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/recruitment" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <RecruitmentDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/debug-user" element={<UserDebug />} />
                <Route path="/test-connectivity" element={<ConnectivityTest />} />

                <Route path="/simple-dashboard" element={<SimpleDashboard />} />
                <Route path="/auth-message" element={<AuthenticationMessage />} />
                <Route path="/clear-cache" element={<ClearCache />} />

                {/* Protected Admin Help Routes */}
                <Route path="/help/admin/dashboard" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <AdminDashboardHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/courses" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <CourseManagementHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/opportunities" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <OpportunityManagementHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/users" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <UserAdministrationHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/lifecycle" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <StudentLifecycleHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/announcements" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <AnnouncementsHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/django" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <DjangoAdminHelp />
                  </ProtectedRoute>
                } />
                <Route path="/help/admin/security" element={
                  <ProtectedRoute requirePrivileged={true}>
                    <SecurityHelp />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
            <Footer />
          </div>
            </Router>
          </ToastProvider>
        </AnnouncementsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
